import { resolveConfig, type ResolvedConfig } from './config';
import { SessionManager } from './session';
import { Recorder } from './recorder';
import { ErrorTracker } from './modules/error-tracker';
import { NetworkInterceptor } from './modules/network-interceptor';
import { ConsoleInterceptor } from './modules/console-interceptor';
import { PerformanceTracker } from './modules/performance-tracker';
import { RageClickDetector } from './modules/rage-click-detector';
import { Breadcrumbs } from './modules/breadcrumbs';
import { NavigationTracker } from './modules/navigation-tracker';
import { ResourceTracker } from './modules/resource-tracker';
import { WebSocketTracker } from './modules/websocket-tracker';
import { inlineFonts } from './modules/font-inliner';
import { PrivacyFilter } from './privacy';
import { Transport } from './transport';
import { resolveAnonId, generateUuid } from './anon-id';
import { sanitizeTrackInput } from './track-limits';
import type { ClintplayConfig, ClintplayEvent, NavigationEventData, TrackEventData } from './types';
import { version } from '../package.json';

// Source of truth is package.json — tsup inlines the value at build
// time. Previously this was a hardcoded string that drifted on every
// version bump and broke the test that asserts they match.
export const VERSION = version;
export const NAVIGATION_SNAPSHOT_DEBOUNCE_MS = 150;
export const NAVIGATION_SNAPSHOT_MIN_INTERVAL_MS = 5000;
export const INITIAL_ASYNC_SNAPSHOT_DELAY_MS = 3000;

let config: ResolvedConfig | null = null;
let sessionManager: SessionManager | null = null;
let recorder: Recorder | null = null;
let errorTracker: ErrorTracker | null = null;
let networkInterceptor: NetworkInterceptor | null = null;
let consoleInterceptor: ConsoleInterceptor | null = null;
let performanceTracker: PerformanceTracker | null = null;
let rageClickDetector: RageClickDetector | null = null;
let breadcrumbs: Breadcrumbs | null = null;
let navigationTracker: NavigationTracker | null = null;
let resourceTracker: ResourceTracker | null = null;
let websocketTracker: WebSocketTracker | null = null;
let privacyFilter: PrivacyFilter | null = null;
let transport: Transport | null = null;
let navigationSnapshotTimer: ReturnType<typeof setTimeout> | null = null;
let initialAsyncSnapshotTimer: ReturnType<typeof setTimeout> | null = null;
let lastNavigationSnapshotTime = 0;

function isPathnameChange(from: string | null, to: string): boolean {
  if (!from) return false;
  try {
    const fromUrl = new URL(from, 'http://localhost');
    const toUrl = new URL(to, 'http://localhost');
    return fromUrl.pathname !== toUrl.pathname;
  } catch {
    const cleanFrom = from.split('?')[0].split('#')[0];
    const cleanTo = to.split('?')[0].split('#')[0];
    return cleanFrom !== cleanTo;
  }
}

function ensureInitialized(): void {
  if (!sessionManager) {
    throw new Error('[Clintplay] SDK not initialized. Call Clintplay.init() first.');
  }
}

function handleEvent(event: ClintplayEvent): boolean {
  if (breadcrumbs && event.type !== 'rrweb') {
    breadcrumbs.add(
      event.type === 'error'
        ? 'error'
        : event.type === 'network'
          ? 'network'
          : event.type === 'rage-click'
            ? 'click'
            : 'custom',
      `${event.type}: ${JSON.stringify(event.data).substring(0, 100)}`
    );
  }

  if (event.type === 'navigation') {
    const navData = event.data as NavigationEventData;
    // Only schedule a full snapshot on genuine pathname changes (not search-param or hash updates),
    // and rate-limit with a minimum interval ceiling to protect the main thread and buffer limits.
    if (navData.trigger !== 'initial' && isPathnameChange(navData.from, navData.to)) {
      if (navigationSnapshotTimer) clearTimeout(navigationSnapshotTimer);
      const now = Date.now();
      const elapsed = now - lastNavigationSnapshotTime;
      const delay =
        elapsed >= 0 && elapsed < NAVIGATION_SNAPSHOT_MIN_INTERVAL_MS
          ? Math.max(NAVIGATION_SNAPSHOT_DEBOUNCE_MS, NAVIGATION_SNAPSHOT_MIN_INTERVAL_MS - elapsed)
          : NAVIGATION_SNAPSHOT_DEBOUNCE_MS;

      navigationSnapshotTimer = setTimeout(() => {
        lastNavigationSnapshotTime = Date.now();
        recorder?.takeFullSnapshot();
        navigationSnapshotTimer = null;
      }, delay);
    }
  }

  return transport ? transport.send(event) : false;
}

/**
 * Puts the final LCP/CLS/INP into the buffer while there is still a flush ahead
 * of them.
 *
 * This has to be registered BEFORE `transport.start()`. DOM listeners run in
 * registration order, `Transport` flushes its buffer on this same event, and the
 * tracker is only constructed further down `init()` — so a listener owned by
 * PerformanceTracker itself would always run SECOND, emitting into a buffer that
 * was just drained, on the one occasion (the page going away) with no later
 * flush to rescue it. The metric this whole change exists to produce would be
 * the one thing lost.
 *
 * Module-level const, not an inline arrow: `removeEventListener` needs the same
 * reference, and `addEventListener` dedupes an identical one, so re-running
 * `init()` cannot stack duplicates.
 */
const flushPerformanceOnHide = (): void => {
  if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
  performanceTracker?.flushFinal();
};

function init(userConfig: ClintplayConfig): void {
  stop();
  config = resolveConfig(userConfig);
  sessionManager = new SessionManager(config.projectId);
  const session = sessionManager.start();

  privacyFilter = new PrivacyFilter({
    networkSanitize: config.networkSanitize,
    blockSelector: config.blockSelector,
    maskAllInputs: config.maskAllInputs,
    maskInputSelector: config.maskInputSelector,
  });

  // Persistent anonymous id — resolved once per init; rides every batch
  // envelope. identify() later ties it to a real user server-side.
  const anonId = resolveAnonId(config.persistence);

  transport = new Transport({
    apiUrl: config.apiUrl,
    projectId: config.projectId,
    sessionId: session.id,
    flushIntervalMs: config.flushIntervalMs,
    flushMaxBytes: config.flushMaxBytes,
    compressEvents: config.compressEvents,
    anonId,
    // Every batch carries the build version so the server can answer
    // "which SDK recorded this session?" — the question behind the
    // "SDK 2.1.3 on Safari 16" diagnosis. VERSION comes from
    // package.json via the bundler (see above), so it can never drift
    // from the artifact that was actually published.
    sdkVersion: VERSION,
  });
  // Order matters — see flushPerformanceOnHide.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', flushPerformanceOnHide);
  }
  transport.start();

  breadcrumbs = new Breadcrumbs();

  recorder = new Recorder(session.id, handleEvent, {
    maskAllInputs: config.maskAllInputs,
    ...(config.maskInputSelector ? { maskInputSelector: config.maskInputSelector } : {}),
    ...(config.blockSelector ? { blockSelector: config.blockSelector } : {}),
    ...(config.ignoreSelector ? { ignoreSelector: config.ignoreSelector } : {}),
    ...config.rrwebConfig,
  });

  // Defer recording start so the initial FullSnapshot captures the
  // rendered DOM shell rather than an empty SPA root element.
  const startRecording = () => {
    if (!recorder) return;
    recorder.start();
    // Inline @font-face assets as data URLs so the replay viewer
    // (clintplay.linhares.sc) can render them without cross-origin
    // fetching back to the recorded site. We take a fresh FullSnapshot
    // the moment inlining completes so the injected <style> is in the
    // initial replay state — without that explicit snapshot, only
    // rrweb's mutation observer would capture the new <style>, and the
    // replay would briefly use fallback fonts at t=0.
    void inlineFonts()
      .then((n) => {
        if (n > 0) recorder?.takeFullSnapshot();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[Clintplay] font-inliner failed', err);
      });
    // One-time follow-up snapshot to capture async-loaded content (data
    // fetched via API after initial render) so the FIRST few seconds of
    // replay are correct. Steady-state seekability is handled separately by
    // the recorder's periodic checkout (DEFAULT_REPLAY_CHECKPOINT_MS).
    if (initialAsyncSnapshotTimer) clearTimeout(initialAsyncSnapshotTimer);
    initialAsyncSnapshotTimer = setTimeout(() => {
      recorder?.takeFullSnapshot();
      initialAsyncSnapshotTimer = null;
    }, INITIAL_ASYNC_SNAPSHOT_DELAY_MS);
  };
  if (document.readyState === 'complete') {
    requestAnimationFrame(() => requestAnimationFrame(startRecording));
  } else {
    window.addEventListener('load', () =>
      requestAnimationFrame(startRecording), { once: true });
  }

  errorTracker = new ErrorTracker(session.id, handleEvent);
  errorTracker.start();

  resourceTracker = new ResourceTracker(session.id, handleEvent);
  resourceTracker.start();

  websocketTracker = new WebSocketTracker(session.id, handleEvent);
  websocketTracker.start();

  if (config.recordNetwork) {
    networkInterceptor = new NetworkInterceptor(
      session.id,
      (event) => {
        const sanitized = privacyFilter!.sanitizeNetworkEvent(event.data as any);
        handleEvent({ ...event, data: sanitized });
      },
      config.apiUrl,
    );
    networkInterceptor.start();
  }

  if (config.recordConsole) {
    consoleInterceptor = new ConsoleInterceptor(session.id, handleEvent);
    consoleInterceptor.start();
  }

  if (config.trackPerformance) {
    performanceTracker = new PerformanceTracker(session.id, handleEvent);
    performanceTracker.start();
  }

  if (config.trackRageClicks) {
    rageClickDetector = new RageClickDetector(session.id, handleEvent);
    rageClickDetector.start();
  }

  if (config.trackNavigation) {
    navigationTracker = new NavigationTracker(session.id, handleEvent);
    navigationTracker.start();
  }
}

function identify(userId: string, traits?: Record<string, unknown>): void {
  ensureInitialized();
  sessionManager!.identify(userId, traits);
  handleEvent({
    type: 'identify',
    timestamp: Date.now(),
    sessionId: sessionManager!.current().id,
    data: { userId, traits },
  });
}

function track(name: string, properties?: Record<string, unknown>): void {
  ensureInitialized();
  const sanitized = sanitizeTrackInput(name, properties);
  if (sanitized.name.length === 0) {
    // eslint-disable-next-line no-console
    console.warn('[Clintplay] track() ignored: event name is empty');
    return;
  }
  if (sanitized.nameTruncated || sanitized.droppedKeys.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Clintplay] track('${sanitized.name}') truncated to fit limits` +
        (sanitized.droppedKeys.length > 0
          ? ` (dropped: ${sanitized.droppedKeys.join(', ')})`
          : ''),
    );
  }
  const data: TrackEventData = {
    name: sanitized.name,
    properties: sanitized.properties,
    eventUuid: generateUuid(),
  };
  handleEvent({
    type: 'track',
    timestamp: Date.now(),
    sessionId: sessionManager!.current().id,
    data,
  });
}

function tag(tagName: string): void {
  ensureInitialized();
  sessionManager!.addTag(tagName);
  handleEvent({
    type: 'tag',
    timestamp: Date.now(),
    sessionId: sessionManager!.current().id,
    data: { tag: tagName },
  });
}

function setMetadata(data: Record<string, unknown>): void {
  ensureInitialized();
  sessionManager!.setMetadata(data);
  handleEvent({
    type: 'metadata',
    timestamp: Date.now(),
    sessionId: sessionManager!.current().id,
    data,
  });
}

function stop(): void {
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', flushPerformanceOnHide);
  }
  if (navigationSnapshotTimer) {
    clearTimeout(navigationSnapshotTimer);
    navigationSnapshotTimer = null;
  }
  if (initialAsyncSnapshotTimer) {
    clearTimeout(initialAsyncSnapshotTimer);
    initialAsyncSnapshotTimer = null;
  }
  recorder?.stop();
  errorTracker?.stop();
  resourceTracker?.stop();
  websocketTracker?.stop();
  networkInterceptor?.stop();
  consoleInterceptor?.stop();
  performanceTracker?.stop();
  rageClickDetector?.stop();
  navigationTracker?.stop();
  transport?.stop();
  sessionManager?.end();

  recorder = null;
  errorTracker = null;
  resourceTracker = null;
  websocketTracker = null;
  networkInterceptor = null;
  consoleInterceptor = null;
  performanceTracker = null;
  rageClickDetector = null;
  navigationTracker = null;
  transport = null;
  sessionManager = null;
  breadcrumbs = null;
  privacyFilter = null;
  config = null;
  lastNavigationSnapshotTime = 0;
}

export const Clintplay = {
  VERSION,
  init,
  identify,
  track,
  tag,
  setMetadata,
  stop,
};

export default Clintplay;
export type { ClintplayConfig, ClintplayEvent, Session } from './types';
